import { useEffect, useState } from 'react';
import { useCurrentUser } from '../../../contexts/UserContext';
import { getUpcomingMeetings } from '../../../services/meetingService';

const DAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function getMonthMatrix(year, month) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  return cells;
}

function dayKey(year, month, day) {
  return `${year}-${month}-${day}`;
}

export default function CalendarWidget() {
  const currentUser = useCurrentUser();
  const userId = currentUser?.id || currentUser?._id;

  const now = new Date();
  const [viewYear, setViewYear] = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth());
  const [meetingsByDay, setMeetingsByDay] = useState(new Map());

  const todayYear = now.getFullYear();
  const todayMonth = now.getMonth();
  const todayDate = now.getDate();

  useEffect(() => {
    if (!userId) return;
    getUpcomingMeetings(userId)
      .then(({ meetings }) => {
        const map = new Map();
        (meetings || []).forEach((m) => {
          const d = new Date(m.scheduledAt);
          const k = dayKey(d.getFullYear(), d.getMonth(), d.getDate());
          if (!map.has(k)) map.set(k, []);
          map.get(k).push(m);
        });
        setMeetingsByDay(map);
      })
      .catch(() => {});
  }, [userId]);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0); }
    else setViewMonth(m => m + 1);
  };

  const monthLabel = new Date(viewYear, viewMonth, 1).toLocaleString('default', {
    month: 'long', year: 'numeric',
  });

  const cells = getMonthMatrix(viewYear, viewMonth);

  return (
    <div className="widget-card calendar-card">
      <div className="calendar-header">
        <p className="eyebrow">{monthLabel}</p>
        <div className="calendar-controls">
          <span onClick={prevMonth} role="button" aria-label="Previous month">‹</span>
          <span onClick={nextMonth} role="button" aria-label="Next month">›</span>
        </div>
      </div>

      <div className="calendar-grid" role="grid" aria-label="Monthly calendar">
        {DAY_LABELS.map((label) => (
          <span key={label} className="calendar-cell calendar-cell--header">
            {label}
          </span>
        ))}
        {cells.map((day, i) => {
          if (!day) {
            return <span key={i} className="calendar-cell calendar-cell--empty" />;
          }

          const isToday = day === todayDate && viewMonth === todayMonth && viewYear === todayYear;
          const key = dayKey(viewYear, viewMonth, day);
          const dayMeetings = meetingsByDay.get(key);
          const hasMeeting = !!dayMeetings;

          const classNames = [
            'calendar-cell',
            isToday ? 'calendar-cell--today' : '',
            hasMeeting ? 'calendar-cell--meeting' : '',
          ].filter(Boolean).join(' ');

          return (
            <div
              key={i}
              className={classNames}
              role="gridcell"
              aria-label={`${monthLabel} ${day}${hasMeeting ? `, ${dayMeetings.length} meeting${dayMeetings.length > 1 ? 's' : ''}` : ''}`}
            >
              {day}
              {hasMeeting && (
                <>
                  <span className="calendar-meeting-dot" aria-hidden="true" />
                  <div className="calendar-meeting-tooltip">
                    {dayMeetings.map((m, idx) => (
                      <p key={idx} className="calendar-meeting-tooltip__item">{m.title}</p>
                    ))}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
